/**
 * LoginPage Component
 *
 * Premium login page with Logto CIAM authentication.
 * Supports: Password + OTP email (via Logto hosted UI)
 * Fallback: Magic Link (when Logto is not configured)
 *
 * Follows LEXE design system with glassmorphism and animations.
 */

import { useState, useCallback, useEffect } from "react";
import logoLight from "@/assets/brand/logo-light.png";
import logoDark from "@/assets/brand/logo-dark.png";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useLogto } from "@logto/react";
import {
  Shield,
  AlertCircle,
  ArrowRight,
  Mail,
  CheckCircle,
  LogIn,
} from "lucide-react";

import { GlassCard } from "@/components/effects/GlassCard";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { useAuthStore } from "@/stores/authStore";
import {
  getOAuthService,
  isOAuthEnabled,
  isLogtoEnabled,
} from "@/services/auth";
import { cn } from "@/lib/utils";
import { useUIStore, selectTheme } from "@/stores/uiStore";

/**
 * Animation variants for staggered entry
 */
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: "easeOut" },
  },
};

/**
 * Multilingual hype phrases - rotates every 5 seconds
 */
const HYPE_PHRASES = [
  {
    lang: "IT",
    text: "Ragione e non rumore, LEXe trasforma il caos legale in decisioni limpide.",
  },
  {
    lang: "PT",
    text: "Razão e não ruído, a LEXe transforma o caos jurídico em decisões cristalinas.",
  },
  {
    lang: "EN",
    text: "Reason, not noise, LEXe turns legal chaos into crystal clear decisions.",
  },
  {
    lang: "FR",
    text: "Raison, pas de bruit, LEXe transforme le chaos juridique en décisions limpides.",
  },
  {
    lang: "ES",
    text: "Razón y no ruido, LEXe convierte el caos legal en decisiones cristalinas.",
  },
] as const;

/**
 * Rotating phrase component with fade animation
 */
function RotatingPhrase() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % HYPE_PHRASES.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const phrase = HYPE_PHRASES[index];

  return (
    <div className="h-12 flex items-center justify-center">
      <AnimatePresence mode="wait">
        <motion.p
          key={index}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.5 }}
          className="text-sm text-leo-gray/70 dark:text-leo-gray/60 italic text-center px-4"
        >
          <span className="text-leo-accent/60 font-medium text-xs uppercase tracking-wider mr-1.5">
            {phrase.lang}
          </span>
          {phrase.text}
        </motion.p>
      </AnimatePresence>
    </div>
  );
}

/**
 * Domain-to-Organization mapping for client hint
 * When user accesses from a specific domain, pre-select the corresponding Logto organization
 */
const DOMAIN_ORG_MAP: Record<string, string> = {
  // Stage domains
  "play.lexe.pro": "9gur0a2kpdpw", // IT Consulting Test
  "stage-chat.lexe.pro": "lexe-default-stage", // LEXE Stage Organization
  "lexe-mb.lexe.pro": "qwvi4dz9yjhu", // Studio Legale MB Test
  // Production domain
  "ai.lexe.pro": "vij1h45hy26m", // LEXE Default (PROD)
  // Localhost for development
  localhost: "lexe-default-stage", // Default to LEXE Stage for local dev
};

/**
 * Get organization ID based on current hostname
 */
function getOrganizationFromDomain(): string | undefined {
  const hostname = window.location.hostname;
  return DOMAIN_ORG_MAP[hostname];
}

/**
 * Logto Login Form - Uses useLogto hook (requires LogtoProvider)
 */
function LogtoLoginForm({ onError }: { onError: (error: string) => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Use Logto SDK's own isLoading state for SDK initialization
  const {
    signIn,
    isAuthenticated,
    isLoading: logtoLoading,
    error: logtoError,
  } = useLogto();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Debug: Log Logto SDK state
  useEffect(() => {
    console.log("[Logto] SDK State:", {
      logtoLoading,
      isAuthenticated,
      logtoError,
    });
  }, [logtoLoading, isAuthenticated, logtoError]);

  // Combined loading state: SDK initializing OR submitting sign-in
  const isLoading = logtoLoading || isSubmitting;

  // Redirect if already authenticated via Logto
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleLogtoLogin = useCallback(async () => {
    setIsSubmitting(true);
    try {
      // Get organization hint based on domain for client hint (pre-select org in Logto)
      const organizationHint = getOrganizationFromDomain();
      console.log("[Logto] Login initiated with client hint:", {
        hostname: window.location.hostname,
        organizationHint,
      });

      // Sign in via Logto hosted UI with organization client hint
      // The organization_id parameter pre-selects the organization in Logto login
      // This ensures the JWT token includes organization_id claim for tenant mapping
      await signIn({
        redirectUri: window.location.origin + "/callback",
        // Client hint: pass organization_id to pre-select org and get org-scoped token
        ...(organizationHint && {
          extraParams: { organization_id: organizationHint },
        }),
      });
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to start sign-in");
      setIsSubmitting(false);
    }
  }, [signIn, onError]);

  return (
    <Button
      variant="primary"
      size="lg"
      className="w-full"
      onClick={handleLogtoLogin}
      disabled={isLoading}
      isLoading={isLoading}
      leftIcon={!isLoading ? <LogIn className="w-5 h-5" /> : undefined}
      rightIcon={!isLoading ? <ArrowRight className="w-4 h-4" /> : undefined}
      data-testid="logto-button"
    >
      {t("auth.sign_in", "Sign In")}
    </Button>
  );
}

/**
 * LoginPage Component
 */
export function LoginPage() {
  const theme = useUIStore(selectTheme);
  const loginLogo = theme === "dark" ? logoLight : logoDark;
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isLoading: authLoading, isAuthenticated, initAuth } = useAuthStore();

  // Check if Logto is enabled (determined at build time via env vars)
  const logtoEnabled = isLogtoEnabled();

  // Check auth on mount for legacy auth
  useEffect(() => {
    if (!logtoEnabled) {
      initAuth().then(() => {
        const state = useAuthStore.getState();
        if (state.isAuthenticated) {
          navigate("/", { replace: true });
        }
      });
    }
  }, [initAuth, navigate, logtoEnabled]);

  // Redirect if already authenticated (legacy)
  useEffect(() => {
    if (!logtoEnabled && isAuthenticated) {
      navigate("/", { replace: true });
    }
  }, [isAuthenticated, navigate, logtoEnabled]);

  // Form state (for magic link fallback)
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  // Check for error from callback
  const callbackError = searchParams.get("error");

  // Check if OAuth is enabled (legacy Authentik)
  const oauthEnabled = isOAuthEnabled();

  /**
   * Handle magic link request
   */
  const handleMagicLinkRequest = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!email.trim()) {
        setError(t("auth.email_required", "Please enter your email address"));
        return;
      }

      setError(null);
      setIsSubmitting(true);

      try {
        const response = await fetch("/api/auth/register", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: email.trim() }),
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.detail || "Failed to send magic link");
        }

        setMagicLinkSent(true);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to send magic link",
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [email, t],
  );

  /**
   * Handle OAuth login with Authentik
   */
  const handleOAuthLogin = useCallback(async () => {
    setError(null);
    setIsSubmitting(true);

    try {
      const oauthService = getOAuthService();
      await oauthService.login();
    } catch (err) {
      setError(err instanceof Error ? err.message : "OAuth not configured");
      setIsSubmitting(false);
    }
  }, []);

  // When Logto is enabled, don't use legacy authLoading (never gets set to false)
  const isLoading = logtoEnabled ? isSubmitting : isSubmitting || authLoading;

  return (
    <div
      className={cn(
        "min-h-screen flex items-center justify-center p-4",
        "bg-gradient-to-br from-leo-light via-leo-primary/5 to-leo-light",
        "dark:from-leo-dark dark:via-leo-primary/30 dark:to-leo-dark",
      )}
    >
      {/* Background decorative elements */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 0.1, scale: 1 }}
          transition={{ duration: 1.5 }}
          className="absolute -top-1/4 -right-1/4 w-1/2 h-1/2 rounded-full bg-gradient-to-br from-leo-accent to-transparent blur-3xl"
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 0.1, scale: 1 }}
          transition={{ duration: 1.5, delay: 0.3 }}
          className="absolute -bottom-1/4 -left-1/4 w-1/2 h-1/2 rounded-full bg-gradient-to-tr from-leo-secondary to-transparent blur-3xl"
        />
      </div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="relative w-full max-w-md"
      >
        {/* Logo and branding */}
        <motion.div variants={itemVariants} className="text-center mb-8">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{
              type: "spring",
              stiffness: 200,
              damping: 20,
              delay: 0.1,
            }}
            className="mb-5"
          >
            <img
              src={loginLogo}
              alt="LEXe"
              className="h-24 mx-auto rounded-lg"
            />
          </motion.div>
          <h1 className="text-3xl font-heading font-bold tracking-wide bg-gradient-to-r from-leo-primary via-leo-dark to-leo-primary/80 dark:from-white dark:via-leo-light dark:to-white/80 bg-clip-text text-transparent">
            LEX <span className="italic font-normal">et</span> ratio
          </h1>
          <p className="text-lg font-medium text-leo-secondary dark:text-leo-secondary mt-1.5 tracking-widest uppercase">
            Ratio, non rumor
          </p>
          <div className="mt-3">
            <RotatingPhrase />
          </div>
        </motion.div>

        {/* Login card */}
        <motion.div variants={itemVariants}>
          <GlassCard
            variant="dark"
            padding="lg"
            glow
            className="border border-white/10"
          >
            <AnimatePresence mode="wait">
              {magicLinkSent ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  className="space-y-6 text-center py-4"
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-leo-accent/20 border border-leo-accent/30">
                    <CheckCircle className="w-8 h-8 text-leo-accent" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                      {t("auth.check_email", "Check your email")}
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      {t("auth.magic_link_sent", "We've sent a login link to")}{" "}
                      <span className="text-leo-accent font-medium">
                        {email}
                      </span>
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setMagicLinkSent(false);
                      setEmail("");
                    }}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    {t("auth.use_different_email", "Use a different email")}
                  </Button>
                </motion.div>
              ) : (
                <motion.div
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="space-y-6"
                >
                  {/* Error message */}
                  {(error || callbackError) && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-3 rounded-lg bg-red-500/20 border border-red-500/30 text-red-300 text-sm flex items-start gap-2"
                    >
                      <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>
                        {error ||
                          (callbackError === "invalid_token"
                            ? t(
                                "errors:auth.invalid_token",
                                "Invalid or expired login link",
                              )
                            : callbackError)}
                      </span>
                    </motion.div>
                  )}

                  {/* Login description */}
                  <div className="text-center">
                    <p className="text-muted-foreground text-sm">
                      {logtoEnabled
                        ? t(
                            "auth.logto_description",
                            "Sign in with your email and password",
                          )
                        : t(
                            "auth.login_description",
                            "Enter your email to receive a login link",
                          )}
                    </p>
                  </div>

                  {/* Logto Sign-in Button - Primary when Logto is enabled */}
                  {logtoEnabled && <LogtoLoginForm onError={setError} />}

                  {/* Magic Link Form - Shown when Logto is NOT enabled */}
                  {!logtoEnabled && (
                    <form
                      onSubmit={handleMagicLinkRequest}
                      className="space-y-4"
                    >
                      <div>
                        <Input
                          type="email"
                          placeholder={t(
                            "auth.email_placeholder",
                            "your@email.com",
                          )}
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          disabled={isLoading}
                          className="w-full bg-white dark:bg-white/5 border-border dark:border-white/10 text-foreground dark:text-white placeholder:text-muted-foreground focus:border-leo-accent focus:ring-leo-accent/20"
                          data-testid="email-input"
                        />
                      </div>
                      <Button
                        type="submit"
                        variant="primary"
                        size="lg"
                        className="w-full"
                        disabled={isLoading}
                        isLoading={isLoading}
                        leftIcon={
                          !isLoading ? <Mail className="w-5 h-5" /> : undefined
                        }
                        rightIcon={
                          !isLoading ? (
                            <ArrowRight className="w-4 h-4" />
                          ) : undefined
                        }
                        data-testid="magic-link-button"
                      >
                        {t("auth.send_magic_link", "Send Login Link")}
                      </Button>
                    </form>
                  )}

                  {/* Divider + OAuth Button - only shown when OAuth is enabled (legacy) */}
                  {oauthEnabled && !logtoEnabled && (
                    <>
                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <span className="w-full border-t border-border dark:border-white/10" />
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                          <span className="bg-background px-2 text-muted-foreground">
                            {t("auth.or", "or")}
                          </span>
                        </div>
                      </div>

                      <Button
                        variant="secondary"
                        size="lg"
                        className="w-full"
                        onClick={handleOAuthLogin}
                        disabled={isLoading}
                        leftIcon={<Shield className="w-5 h-5" />}
                        data-testid="oauth-button"
                      >
                        {t("auth.login_with_sso", "Sign in with SSO")}
                      </Button>
                    </>
                  )}

                  {/* Request Access Link - only show when Logto is NOT enabled */}
                  {!logtoEnabled && (
                    <div className="text-center pt-4 border-t border-border dark:border-white/10">
                      <span className="text-sm text-muted-foreground">
                        {t("auth.no_account", "Don't have an account?")}{" "}
                        <Link
                          to="/request-access"
                          className="text-leo-accent hover:text-leo-accent/80 font-medium transition-colors"
                        >
                          {t("auth.request_access", "Request Access")}
                        </Link>
                      </span>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </GlassCard>
        </motion.div>

        {/* Footer */}
        <motion.p
          variants={itemVariants}
          className="text-center text-xs text-muted-foreground/60 mt-6"
        >
          LEXe Pro &middot; Normativa. Verificata. Accessibile.
        </motion.p>
      </motion.div>
    </div>
  );
}

export default LoginPage;
